import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Autocomplete,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { BANK_PAYBILLS } from '../settings/bankPaybills';

const OnboardingForm = ({
  formData,
  errors,
  isLoading,
  onInputChange,
  onPhoneBlur,
  onUsernameBlur,
  onSubdomainBlur,
  onSubmit,
  showRetry = false,
  onRetry,
  usernameStatus,
}) => {
  const handleSubdomainChange = (value) => {
    // Convert to lowercase and remove invalid characters
    const cleaned = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/--+/g, '-');
    onInputChange('subdomain', cleaned);
  };

  const handleUsernameChange = (value) => {
    // Convert to lowercase and remove invalid characters (allow letters, numbers, underscores)
    const cleaned = value
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '');
    onInputChange('username', cleaned);
  };

  const handleSelectChange = (field) => (event) => {
    onInputChange(field, event.target.value);
  };

  const handleTextChange = (field) => (event) => {
    onInputChange(field, event.target.value);
  };

  // Get username field end adornment based on status
  const getUsernameEndAdornment = () => {
    if (usernameStatus === 'checking') {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} sx={{ color: '#0b74c1' }} />
        </InputAdornment>
      );
    }
    if (usernameStatus === 'available') {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon sx={{ color: '#19b594', fontSize: '1.25rem' }} />
        </InputAdornment>
      );
    }
    if (usernameStatus === 'taken') {
      return (
        <InputAdornment position="end">
          <ErrorIcon sx={{ color: '#e74c3c', fontSize: '1.25rem' }} />
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: { xs: 2, sm: 3 } }}>
      <TextField
        fullWidth
        label="Full Name"
        value={formData.name}
        onChange={(e) => onInputChange('name', e.target.value)}
        error={!!errors.name}
        helperText={errors.name || 'Enter your full name'}
        margin="normal"
        required
        disabled={isLoading}
        size="medium"
        sx={{
          '& .MuiInputLabel-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
          },
          '& .MuiInputBase-input': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon sx={{ color: '#0b74c1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={formData.email}
        onChange={(e) => onInputChange('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email || 'This will be your login email'}
        margin="normal"
        required
        disabled={isLoading}
        size="medium"
        sx={{
          '& .MuiInputLabel-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
          },
          '& .MuiInputBase-input': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon sx={{ color: '#0b74c1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Phone Number"
        value={formData.phone}
        onChange={(e) => onInputChange('phone', e.target.value)}
        onBlur={onPhoneBlur}
        error={!!errors.phone}
        helperText={errors.phone || 'Enter your phone number (we\'ll automatically add country code)'}
        placeholder="0712345678 or +254712345678"
        margin="normal"
        required
        disabled={isLoading}
        size="medium"
        sx={{
          '& .MuiInputLabel-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
          },
          '& .MuiInputBase-input': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon sx={{ color: '#0b74c1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Username"
        value={formData.username}
        onChange={(e) => handleUsernameChange(e.target.value)}
        onBlur={onUsernameBlur}
        error={!!errors.username || usernameStatus === 'taken'}
        helperText={
          errors.username ? (
            errors.username
          ) : usernameStatus === 'taken' ? (
            <Typography 
              component="span"
              sx={{ 
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#e74c3c',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              }}
            >
              This username is already taken. Please choose another.
            </Typography>
          ) : usernameStatus === 'available' ? (
            <Typography 
              component="span"
              sx={{ 
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#19b594',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              }}
            >
              Username is available!
            </Typography>
          ) : usernameStatus === 'checking' ? (
            'Checking availability...'
          ) : (
            'Choose a unique username for your account (lowercase letters, numbers, underscores)'
          )
        }
        placeholder="john_doe"
        margin="normal"
        required
        disabled={isLoading}
        size="medium"
        FormHelperTextProps={{
          sx: {
            margin: '8px 14px 0',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
          }
        }}
        sx={{
          '& .MuiInputLabel-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
          },
          '& .MuiInputBase-input': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <AccountCircleIcon sx={{ color: '#0b74c1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
          endAdornment: getUsernameEndAdornment(),
        }}
      />

      <TextField
        fullWidth
        label="Preferred Subdomain e.g smartnetworks.nasradius.com"
        value={formData.subdomain}
        onChange={(e) => handleSubdomainChange(e.target.value)}
        onBlur={onSubdomainBlur}
        error={!!errors.subdomain}
        helperText={
          errors.subdomain ? (
            errors.subdomain
          ) : formData.subdomain ? (
            <Typography 
              component="span"
              sx={{ 
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#19b594',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              }}
            >
              Your domain: https://{formData.subdomain}.nasradius.com
            </Typography>
          ) : (
            'Choose a unique subdomain for your dashboard'
          )
        }
        placeholder="smartnetworks"
        margin="normal"
        required
        disabled={isLoading}
        size="medium"
        FormHelperTextProps={{
          sx: {
            margin: formData.subdomain ? '8px 14px 0' : '8px 14px 0',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
          }
        }}
        sx={{
          '& .MuiInputLabel-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
          },
          '& .MuiInputBase-input': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography sx={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif", color: '#666', fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' } }}>
                https://
              </Typography>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif", color: '#666', fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' } }}>
                .nasradius.com
              </Typography>
            </InputAdornment>
          ),
        }}
      />

      <Divider sx={{ my: 2 }} />

      <FormControl fullWidth sx={{ mb: 2 }} required>
        <InputLabel>Payment Provider</InputLabel>
        <Select
          name="provider"
          value={formData.provider}
          onChange={handleSelectChange('provider')}
          label="Payment Provider"
          disabled={isLoading}
        >
          <MenuItem value="M-PESA">M-PESA (Use your own M-PESA credentials)</MenuItem>
          <MenuItem value="Kopokopo">Kopokopo (Use your own Kopokopo credentials)</MenuItem>
          <MenuItem value="NasRadiusPay">NasRadiusPay (Managed payment service)</MenuItem>
        </Select>
      </FormControl>

      {formData.provider === 'M-PESA' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel>Mode</InputLabel>
            <Select
              name="mode"
              value={formData.mode}
              onChange={handleSelectChange('mode')}
              label="Mode"
              disabled={isLoading}
            >
              <MenuItem value="Till">Till</MenuItem>
              <MenuItem value="Paybill">Paybill</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="mpesa_consumer_key"
            label="M-PESA Consumer Key"
            fullWidth
            variant="outlined"
            value={formData.mpesa_consumer_key}
            onChange={handleTextChange('mpesa_consumer_key')}
            error={!!errors.mpesa_consumer_key}
            helperText={errors.mpesa_consumer_key || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="mpesa_consumer_secret"
            label="M-PESA Consumer Secret"
            fullWidth
            variant="outlined"
            type="password"
            value={formData.mpesa_consumer_secret}
            onChange={handleTextChange('mpesa_consumer_secret')}
            error={!!errors.mpesa_consumer_secret}
            helperText={errors.mpesa_consumer_secret || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="mpesa_shortcode"
            label="M-PESA Shortcode"
            fullWidth
            variant="outlined"
            value={formData.mpesa_shortcode}
            onChange={handleTextChange('mpesa_shortcode')}
            error={!!errors.mpesa_shortcode}
            helperText={errors.mpesa_shortcode || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="mpesa_till_number"
            label="M-PESA Till Number"
            fullWidth
            variant="outlined"
            value={formData.mpesa_till_number}
            onChange={handleTextChange('mpesa_till_number')}
            error={!!errors.mpesa_till_number}
            helperText={errors.mpesa_till_number || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="mpesa_passkey"
            label="M-PESA Passkey"
            fullWidth
            variant="outlined"
            type="password"
            value={formData.mpesa_passkey}
            onChange={handleTextChange('mpesa_passkey')}
            error={!!errors.mpesa_passkey}
            helperText={errors.mpesa_passkey || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
        </>
      )}

      {formData.provider === 'Kopokopo' && (
        <>
          <TextField
            margin="dense"
            name="kopokopo_client_id"
            label="Kopokopo Client ID"
            fullWidth
            variant="outlined"
            value={formData.kopokopo_client_id}
            onChange={handleTextChange('kopokopo_client_id')}
            error={!!errors.kopokopo_client_id}
            helperText={errors.kopokopo_client_id || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="kopokopo_client_secret"
            label="Kopokopo Client Secret"
            fullWidth
            variant="outlined"
            type="password"
            value={formData.kopokopo_client_secret}
            onChange={handleTextChange('kopokopo_client_secret')}
            error={!!errors.kopokopo_client_secret}
            helperText={errors.kopokopo_client_secret || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
          <TextField
            margin="dense"
            name="kopokopo_stk_till_number"
            label="Kopokopo STK Till Number"
            fullWidth
            variant="outlined"
            value={formData.kopokopo_stk_till_number}
            onChange={handleTextChange('kopokopo_stk_till_number')}
            error={!!errors.kopokopo_stk_till_number}
            helperText={errors.kopokopo_stk_till_number || ''}
            sx={{ mb: 2 }}
            disabled={isLoading}
            required
          />
        </>
      )}

      {formData.provider === 'NasRadiusPay' && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>NasRadiusPay</strong> is a managed payment service for quick setup.
            </Typography>
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel>Payment Channel</InputLabel>
            <Select
              name="channel"
              value={formData.channel}
              onChange={handleSelectChange('channel')}
              label="Payment Channel"
              disabled={isLoading}
            >
              <MenuItem value="BuyGoods">BuyGoods Till</MenuItem>
              <MenuItem value="PayBill">PayBill</MenuItem>
              <MenuItem value="BankPayBill">Bank PayBill</MenuItem>
            </Select>
          </FormControl>

          {formData.channel === 'BuyGoods' && (
            <TextField
              margin="dense"
              name="shortcode"
              label="Shortcode"
              fullWidth
              variant="outlined"
              value={formData.shortcode}
              onChange={handleTextChange('shortcode')}
              error={!!errors.shortcode}
              helperText={errors.shortcode || ''}
              sx={{ mb: 2 }}
              disabled={isLoading}
              required
            />
          )}

          {formData.channel === 'PayBill' && (
            <>
              <TextField
                margin="dense"
                name="paybill_number"
                label="Paybill Number"
                fullWidth
                variant="outlined"
                value={formData.paybill_number}
                onChange={handleTextChange('paybill_number')}
                error={!!errors.paybill_number}
                helperText={errors.paybill_number || ''}
                sx={{ mb: 2 }}
                disabled={isLoading}
                required
              />
              <TextField
                margin="dense"
                name="account_number"
                label="Account Number"
                fullWidth
                variant="outlined"
                value={formData.account_number}
                onChange={handleTextChange('account_number')}
                error={!!errors.account_number}
                helperText={errors.account_number || ''}
                sx={{ mb: 2 }}
                disabled={isLoading}
                required
              />
            </>
          )}

          {formData.channel === 'BankPayBill' && (
            <>
              <Autocomplete
                options={BANK_PAYBILLS}
                getOptionLabel={(option) => option.name}
                value={BANK_PAYBILLS.find(bank => bank.name === formData.bank_name) || null}
                onChange={(_, newValue) => {
                  onInputChange('bank_name', newValue ? newValue.name : '');
                  onInputChange('paybill_number', newValue ? newValue.code : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bank"
                    variant="outlined"
                    sx={{ mb: 2 }}
                    error={!!errors.bank_name}
                    helperText={errors.bank_name || ''}
                    disabled={isLoading}
                    required
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Typography variant="body2">{option.name} ({option.code})</Typography>
                    </li>
                  );
                }}
                filterOptions={(options, state) => {
                  const filtered = options.filter(option =>
                    option.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                    option.code.includes(state.inputValue)
                  );
                  return filtered;
                }}
                disabled={isLoading}
              />
              <TextField
                margin="dense"
                name="paybill_number"
                label="Bank Paybill Number"
                fullWidth
                variant="outlined"
                disabled
                value={formData.paybill_number}
                onChange={onInputChange}
                error={!!errors.paybill_number}
                helperText={errors.paybill_number || 'Auto-filled based on selected bank'}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="account_number"
                label="Account Number"
                fullWidth
                variant="outlined"
                value={formData.account_number}
                onChange={handleTextChange('account_number')}
                error={!!errors.account_number}
                helperText={errors.account_number || ''}
                sx={{ mb: 2 }}
                disabled={isLoading}
                required
              />
            </>
          )}
        </>
      )}

      <Box sx={{ 
        mt: { xs: 3, sm: 4 }, 
        display: 'flex', 
        gap: { xs: 1.5, sm: 2 }, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        {showRetry && onRetry && (
          <Button
            variant="outlined"
            onClick={onRetry}
            disabled={isLoading}
            fullWidth
            sx={{ 
              minWidth: { xs: '100%', sm: 120 },
              maxWidth: { xs: '100%', sm: 'none' },
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontWeight: 600,
              borderRadius: '8px',
              borderColor: '#19b594',
              color: '#19b594',
              textTransform: 'none',
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              py: { xs: 1.25, sm: 1.5 },
              '&:hover': {
                borderColor: '#17a085',
                backgroundColor: 'rgba(25, 181, 148, 0.04)',
              },
            }}
          >
            Go Back
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading}
          fullWidth
          sx={{ 
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 'none' },
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            borderRadius: '8px',
            background: 'linear-gradient(90deg, #0a66bb, #19b594)',
            color: '#fff',
            textTransform: 'none',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
            boxShadow: '0px 4px 12px rgba(11, 116, 193, 0.2)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(90deg, #0b74c1, #219cac)',
              transform: 'translateY(-2px)',
              boxShadow: '0px 6px 16px rgba(11, 116, 193, 0.3)',
            },
            '&:disabled': {
              background: 'linear-gradient(90deg, #0a66bb, #19b594)',
              opacity: 0.6,
            },
          }}
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </Box>
    </Box>
  );
};

export default OnboardingForm;

